import React from "react";
import { motion } from "framer-motion";
import "./Methodology.css";

// Animation: Subtle, professional fade-in
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

function Methodology() {
  return (
    <div className="methodology-container">
      <div className="methodology-paper">
        {/* Header Section */}
        <motion.header
          className="methodology-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h1>Data and Methodology</h1>
        </motion.header>

        {/* Linear Content Sections */}
        <motion.div
          className="methodology-sections"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* 1. Data Sources */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h2>1. Data Sources</h2>
            <p className="section-intro">
              <em>What data underpins the facility and climate indicators shown in this dashboard?</em>
            </p>

            <h3>Facilities data</h3>
            <p>
              The dashboard uses a facilities dataset shared by Open Supply Hub, where each record represents a distinct industrial or commercial facility. Each facility is identified using a stable facility identifier (<code>os_id</code>) and includes geographic coordinates (latitude and longitude), facility type, associated brands, and an estimate of the number of workers.
            </p>
            <p>
              In several cases, facility ownership is recorded at the level of a parent company whose name may not be widely recognizable. To improve interpretability for dashboard users, a <strong>manual mapping step</strong> was applied to associate such parent entities with more recognizable brand names. This mapping is intended to improve readability and does not alter the underlying facility record.
            </p>
            <p>
              The <strong>number of workers</strong> field provides contextual information on the scale of labour potentially exposed to local climate conditions. While this value does not directly influence climate calculations, it is used to support the interpretation of potential climate impacts across facilities of different sizes.
            </p>

            <h3>Climate data</h3>
            <p>
              The dashboard integrates climate data from two independent sources, each selected for the variable it provides at suitable spatial and temporal resolution:
            </p>
            <ul>
              <li>
                <strong>Temperature data</strong> is sourced from the Indian Meteorological Department (IMD) gridded datasets, accessed using the <code>imdlib</code> Python library (<a href="https://imdlib.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">https://imdlib.readthedocs.io/en/latest/</a>). This source provides gridded temperature observations across India.
              </li>
              <li>
                <strong>Humidity data</strong> is sourced from Open-Meteo, which provides historical relative humidity values derived from reanalysis datasets.
              </li>
            </ul>
            <p>
              Both climate datasets span the calendar year 2025, subject to data availability by location and variable.
            </p>
          </motion.div>

          {/* 2. Data Processing and Integration */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h2>2. Data Processing and Integration</h2>
            <p className="section-intro">
              <em>How are facility locations linked to daily climate conditions?</em>
            </p>

            <h3>Facility preprocessing</h3>
            <p>
              Facilities are first cleaned and standardized to ensure consistent identifiers, valid geographic coordinates, and normalized brand representations where multiple values are present. Each facility’s latitude and longitude serve as the spatial reference point for all climate data associations.
            </p>

            <h3>Climate data extraction and transformation</h3>

            <h4>Temperature</h4>
            <p>
              Temperature data is obtained from the <strong>India Meteorological Department (IMD) gridded dataset</strong> using the <code>imdlib</code> Python library (<a href="https://imdlib.readthedocs.io/en/latest/" target="_blank" rel="noopener noreferrer">https://imdlib.readthedocs.io/en/latest/</a>). IMDLIB provides direct access to daily gridded minimum (<code>tmin</code>) and maximum (<code>tmax</code>) temperature values at each grid location for the period of interest.
            </p>
            <p>For each facility:</p>
            <ul>
              <li>The daily minimum and maximum temperature values nearest to the facility’s coordinates are extracted.</li>
              <li>A daily average temperature is computed as a simple average of the daily minimum and maximum values.</li>
              <li>These daily values form the temperature component of the climate dataset used in the dashboard.</li>
            </ul>
            <p>
              This approach leverages IMDLIB’s support for IMD’s <strong>daily gridded temperature data</strong>, which are pre-processed at the source and organized in a daily sequence, eliminating the need to extract or aggregate sub-daily temperature values.
            </p>

            <h4>Humidity</h4>
            <p>
              Humidity data is extracted at an hourly resolution from Open-Meteo for each facility location. Similar to temperature processing:
            </p>
            <ul>
              <li>hourly relative humidity values are retrieved,</li>
              <li>daily minimum, maximum, and average humidity values are computed,</li>
              <li>daily summaries are used for integration with temperature data.</li>
            </ul>

            <h3>Spatial matching</h3>
            <p>
              Climate observations are associated with facilities using a coordinate-based approach:
            </p>
            <ul>
              <li>Climate values are extracted at the facility’s geographic coordinates wherever possible.</li>
              <li>When an exact coordinate match is not available due to gridded data resolution, values representative of the nearest available grid point are used.</li>
            </ul>
            <p>
              As a result, climate values represent <strong>conditions in the immediate vicinity of each facility</strong>, rather than administrative or regional averages.
            </p>

            <h3>Temporal alignment and integration</h3>
            <p>
              Daily temperature and humidity datasets are joined using the facility identifier and calendar date. This produces a unified daily climate record for each facility containing temperature and humidity metrics.
            </p>
            <p>The dashboard displays:</p>
            <ul>
              <li><strong>near real-time daily conditions</strong> in the side panel, reflecting climate data available up to <strong>T–1 day (yesterday)</strong>, and</li>
              <li><strong>aggregated seasonal and long-term trends</strong> in the main visualizations.</li>
            </ul>
            <p>
              This distinction ensures users can differentiate between recent conditions and longer-term climate patterns.
            </p>
          </motion.div>

          {/* 3. Aggregation and Trend Computation */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h2>3. Aggregation and Trend Computation</h2>
            <p className="section-intro">
              <em>How are daily climate observations transformed into seasonal and long-term trends?</em>
            </p>
            <p>
              Daily climate records form the foundational dataset for all visualizations. To highlight broader climate patterns, the data is aggregated across multiple temporal scales.
            </p>

            <h3>Seasonal aggregation</h3>
            <p>Seasons are defined using standard meteorological groupings:</p>
            <ul>
              <li><strong>DJF</strong>: December–January–February</li>
              <li><strong>MAM</strong>: March–April–May</li>
              <li><strong>JJA</strong>: June–July–August</li>
              <li><strong>SON</strong>: September–October–November</li>
            </ul>
            <p>
              For each facility, seasonal values are computed as simple arithmetic means of daily observations within the corresponding months.
            </p>

            <h3>Monthly and yearly trends</h3>
            <p>
              Monthly and yearly averages are computed by grouping daily climate records by calendar month and year. These aggregations are used to surface longer-term trends in temperature and humidity and to support comparisons across facilities and regions.
            </p>
            <p>
              All aggregated values represent <strong>average climate conditions near facilities</strong> and are intended to provide contextual insights rather than precise on-site measurements.
            </p>
          </motion.div>

          {/* 4. Limitations and Considerations */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h2>4. Limitations and Considerations</h2>
            <p className="section-intro">
              <em>What should users keep in mind when interpreting the results?</em>
            </p>
            <ul>
              <li>Climate values are derived from gridded datasets and represent near-facility conditions rather than direct measurements taken at facility sites.</li>
              <li>Temperature and humidity data are derived from distinct gridded datasets that differ in spatial resolution and interpolation methodology. As a result, facility-level heat stress estimates reflect modelled approximations rather than direct on-site measurements.</li>
              <li>Short-term variability in daily conditions should be interpreted separately from seasonal and long-term trends.</li>
              <li>The <strong>number of workers</strong> field provides contextual scale but does not capture differences in working conditions, exposure duration, or adaptive measures at individual facilities.</li>
            </ul>
            <p>
              These considerations are important when using the dashboard to assess potential climate impacts across facilities and labour populations.
            </p>
          </motion.div>

          {/* 5. Tech */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h2>5. Tech</h2>
            <p>
              The dashboard is built on a spatial database infrastructure using Supabase (PostgreSQL with PostGIS) to manage facility and climate data. Data processing and transformation are handled in Python using pandas, including the integration and aggregation of temperature and humidity datasets sourced from IMD (via imdlib) and Open-Meteo. The front-end interface is developed using Mapbox for spatial visualization, supported by a combination of JavaScript and React components to enable interactive exploration and filtering of the data.
            </p>
          </motion.div>

          {/* Legend Table */}
          <motion.div className="methodology-section" variants={itemVariants}>
            <h3>Legend</h3>
            <div className="methodology-table-wrapper">
              <table className="methodology-table">
                <thead>
                  <tr>
                    <th>Range (°C)</th>
                    <th>Description (can be modified)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>20–24</td><td>Cool/comfortable: typical minimums, no heat stress</td></tr>
                  <tr><td>25–29</td><td>Mild: warm but generally safe for most activities</td></tr>
                  <tr><td>30–31</td><td>Warm threshold: start of noticeable heat</td></tr>
                  <tr><td>32–33</td><td>Rising stress: caution for prolonged outdoor work</td></tr>
                  <tr><td>34–35</td><td>High caution: heat stress risk increases</td></tr>
                  <tr><td>36–37</td><td>Severe heat stress: limit strenuous activity</td></tr>
                  <tr><td>38–39</td><td>Very high risk: dangerous for unprotected exposure</td></tr>
                  <tr><td>40–44</td><td>Extreme: heat-related illness likely without action</td></tr>
                  <tr><td>45+</td><td>Critical: life-threatening heat, avoid all exposure</td></tr>
                </tbody>
              </table>
            </div>
            <p>
              LIMITATION: The temperature legend and associated heat classifications are based solely on ambient air temperature. Since humidity is not incorporated, the map does not reflect composite heat stress metrics such as heat index or wet-bulb temperature.
            </p>
          </motion.div>
        </motion.div>

        {/* Footer */}
        <footer className="methodology-footer">
          <p>© {new Date().getFullYear()} HeatWatch Foundation. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}

export default Methodology;
